import { ArrowLeft, Plus, Star, Trash2, Users } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  useCreateTourShareTemplateMutation,
  useDeleteTourShareTemplateMutation,
  useGetTourShareTemplatesQuery,
  useSetDefaultTourShareTemplateMutation,
  useUpdateTourShareTemplateMutation,
} from "../../../../../app/services/book/tour_share_templates"
import TemplateEditorForm from "../../../save_tours/components/TemplateEditorForm"

const TemplatesTab = ({ active }) => {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(null)
  const [editorExpanded, setEditorExpanded] = useState(false)

  const openEditing = (value) => {
    setEditorExpanded(false)
    setEditing(value)
  }

  const { data, isLoading } = useGetTourShareTemplatesQuery(undefined, { skip: !active })
  const templates = data?.templates || []

  const [createTemplate] = useCreateTourShareTemplateMutation()
  const [updateTemplate] = useUpdateTourShareTemplateMutation()
  const [deleteTemplate] = useDeleteTourShareTemplateMutation()
  const [setDefault] = useSetDefaultTourShareTemplateMutation()

  const handleSave = async (values) => {
    if (editing === "new") {
      await createTemplate(values).unwrap()
    } else {
      await updateTemplate({ id: editing.id, ...values }).unwrap()
    }
    openEditing(null)
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t("saved_tours.share.confirm_delete"))) return
    await deleteTemplate(id).unwrap()
  }

  return (
    <div>
      {editing && (
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => openEditing(null)}
            className="p-1.5 rounded-lg text-[#9AA5B4] hover:bg-[#F4F6F9] transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <h3 className="text-sm font-semibold text-[#1F2937]">
            {t("saved_tours.share.edit_template")}
          </h3>
        </div>
      )}

      {editing ? (
        <TemplateEditorForm
          initial={editing === "new" ? null : editing}
          onSave={handleSave}
          onCancel={() => openEditing(null)}
          expanded={editorExpanded}
          onToggleExpanded={() => setEditorExpanded((v) => !v)}
        />
      ) : (
        <>
          <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
            {isLoading && (
              <div className="text-sm text-[#9AA5B4] py-6 text-center">
                {t("saved_tours.share.loading")}
              </div>
            )}

            {templates.map((tpl) => {
              const isShared = !tpl.own
              const canDelete = tpl.own && !tpl.is_default

              return (
                <div
                  key={tpl.id}
                  onClick={() => openEditing(tpl)}
                  className="flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl
                             border border-[#E7EAEF] hover:bg-[#F4F6F9] cursor-pointer transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="text-sm font-medium text-[#1F2937] truncate">{tpl.name}</div>
                      {isShared && (
                        <span
                          className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md
                                     bg-[#F4F6F9] text-[10px] font-medium text-[#9AA5B4]"
                        >
                          <Users size={10} />
                          {t("saved_tours.share.shared_template")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#9AA5B4]">{tpl.format}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {tpl.own && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setDefault(tpl.id)
                        }}
                        title={t("saved_tours.share.set_default")}
                        className={`p-1.5 rounded-lg transition-colors ${
                          tpl.is_default
                            ? "text-amber-500"
                            : "text-[#9AA5B4] hover:bg-white hover:text-[#1F2937]"
                        }`}
                      >
                        <Star size={16} fill={tpl.is_default ? "currentColor" : "none"} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(tpl.id)
                        }}
                        title={t("saved_tours.share.delete")}
                        className="p-1.5 rounded-lg text-[#9AA5B4] hover:bg-white hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}

            {!isLoading && templates.length === 0 && (
              <div className="text-sm text-[#9AA5B4] py-6 text-center">
                {t("saved_tours.share.no_templates")}
              </div>
            )}
          </div>

          <button
            onClick={() => openEditing("new")}
            className="mt-4 w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl
                       border border-dashed border-[#E7EAEF] text-sm font-medium text-[#1F2937]
                       hover:bg-[#F4F6F9] transition-colors"
          >
            <Plus size={16} />
            {t("saved_tours.share.new_template")}
          </button>
        </>
      )}
    </div>
  )
}

export default TemplatesTab
